import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { touchTargetSx } from "_shared/_views/touch-target";
import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { levelOptions, resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import AddAccessTargetStep, { targetNounOf } from "permissions/_views/people/add-access-target-step";
import FieldAccessDialog from "permissions/_views/people/field-access-dialog";
import { isFieldKind } from "permissions/_views/people/person-record-grants-model";
import type { GrantTarget } from "permissions/_views/people/use-person-grant-changes";
import useRecordTargetSearch, { type RecordTarget } from "permissions/_views/people/use-record-target-search";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import type { FieldAccessValues } from "permissions/_views/people/field-access-dialog";
import {
  type GrantLevel,
  HELD,
  LEVEL_CONTROL_TITLE,
  kindAllows,
  kindLabel,
} from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import { useEffect, useId, useRef, useState } from "react";

/**
 * Add access, kind first: what the person may do, then which record for a per-record kind, then how
 * much, then Grant (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Adding, removing, changing").
 *
 * Opened from a person page, so the person is fixed. The kinds are grouped by area with the "may …"
 * sentence under each, so "sign" finds signing authority without knowing it is a kind of
 * organization access. A per-record kind carries a "Needs a record" chip; choosing it opens the target
 * step (`AddAccessTargetStep`). One proposal and one work agreement have no level: choosing their
 * record opens the field editor in place of the level step, and its button is the Grant.
 *
 * A kind or a record the person already holds is marked, and choosing it changes the grant they hold
 * — a `PUT` on that row, never a second grant.
 */

export const NEEDS_A_RECORD = "Needs a record";
export const ALREADY_HELD = "Held";

function matches(kind: PermissionKind, text: string): boolean {
  const haystack = `${kindLabel(kind)} ${kindAllows(kind)} ${kind.area}`.toLowerCase();
  return text
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

function sameLevel(a: GrantLevel, b: GrantLevel): boolean {
  return a.accessLevel === b.accessLevel && a.scope === b.scope;
}

function defaultLevelOf(kind: PermissionKind): GrantLevel {
  return {
    accessLevel: (kind.default?.accessLevel ?? undefined) as GrantLevel["accessLevel"],
    scope: (kind.default?.scope ?? undefined) as GrantLevel["scope"],
  };
}

/** The fields a held proposal or work agreement grant confers, which the field editor starts from. */
function heldFields(permission: Permission | undefined): FieldAccessValues {
  return (permission?.resource as { fields?: FieldAccessValues } | undefined)?.fields ?? {};
}

export type AddAccessDialogProps = Readonly<{
  open: boolean;
  /** The person, whose grants on the records the target step finds are looked up. */
  userId: Guid;
  personName: string;
  areas: readonly string[];
  kinds: readonly PermissionKind[];
  /** The live grant the person holds of each kind, by slug: choosing that kind changes its level. */
  held: ReadonlyMap<string, Permission>;
  mayAdministerPlatform: boolean;
  onClose: () => void;
  /**
   * Resolves true once the server has answered with the grant, which closes the dialog. A per-record
   * kind's grant names its `target`.
   */
  onGrant: (kind: PermissionKind, level: GrantLevel | undefined, target?: GrantTarget) => Promise<boolean>;
  onChangeLevel: (permission: Permission, kind: PermissionKind, level: GrantLevel) => Promise<boolean>;
  /** Sets the fields of a proposal or work agreement grant the person already holds. */
  onChangeFields: (permission: Permission, kind: PermissionKind, fields: FieldAccessValues) => Promise<boolean>;
}>;

export default function AddAccessDialog(props: AddAccessDialogProps) {
  const { open, userId, personName, areas, kinds, held, mayAdministerPlatform, onClose, onGrant, onChangeLevel, onChangeFields } =
    props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const titleId = useId();
  const levelHeadingId = useId();
  const [text, setText] = useState("");
  const [chosen, setChosen] = useState<PermissionKind>();
  const [targetText, setTargetText] = useState("");
  const [chosenTarget, setChosenTarget] = useState<RecordTarget>();
  const [editingFields, setEditingFields] = useState(false);
  const [level, setLevel] = useState<GrantLevel>({});
  const [saving, setSaving] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const levelRef = useRef<HTMLFieldSetElement>(null);
  const targetSearch = useRecordTargetSearch(userId, chosen?.perRecord ? chosen : undefined, targetText);
  // The chosen record as the search last answered it, so a change after a conflict sends the grant's
  // current version rather than the one held when the record was chosen.
  const target = chosenTarget && (targetSearch.targets?.find((record) => record.id === chosenTarget.id) ?? chosenTarget);

  // On a phone the level step opens below the records; a record chosen brings it into view.
  useEffect(() => {
    if (chosenTarget) levelRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [chosenTarget]);

  const offered = kinds.filter((kind) => !kind.retired && kind.grantable && (!kind.superUserOnly || mayAdministerPlatform));
  const visible = offered.filter((kind) => matches(kind, text.trim()));
  const perRecord = chosen?.perRecord ?? false;
  const byField = chosen ? isFieldKind(chosen) : false;
  let heldGrant: Permission | undefined;
  if (perRecord) heldGrant = target?.held;
  else if (chosen) heldGrant = held.get(chosen.slug);
  const currentLevel = heldGrant && !byField ? resourceLevel(heldGrant.resource) : undefined;
  const options = chosen && !byField ? levelOptions(chosen, currentLevel ?? level, mayAdministerPlatform) : [];
  const unchanged = currentLevel !== undefined && sameLevel(currentLevel, level);
  // The level is drawn once there is something to set it on: the kind, or for a per-record kind its record.
  const showLevel = chosen !== undefined && !byField && (!perRecord || target !== undefined);
  const waitingForTarget = perRecord && !target;
  const noun = chosen ? targetNounOf(chosen) : "";

  const steps = perRecord ? ["What", `Which ${noun}`, byField ? "Fields" : "How much", "Grant"] : ["What", "How much", "Grant"];
  let activeStep = 0;
  if (chosen) activeStep = waitingForTarget ? 1 : steps.length - 2;

  const reset = () => {
    setText("");
    setChosen(undefined);
    setTargetText("");
    setChosenTarget(undefined);
    setEditingFields(false);
    setLevel({});
    setSaving(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const choose = (kind: PermissionKind) => {
    setChosen(kind);
    setTargetText("");
    setChosenTarget(undefined);
    const holding = kind.perRecord ? undefined : held.get(kind.slug);
    setLevel(holding ? resourceLevel(holding.resource) : defaultLevelOf(kind));
  };

  const chooseTarget = (record: RecordTarget) => {
    if (!chosen) return;
    setChosenTarget(record);
    setLevel(record.held ? resourceLevel(record.held.resource) : defaultLevelOf(chosen));
    if (isFieldKind(chosen)) setEditingFields(true);
  };

  const submit = async () => {
    if (!chosen) return;
    setSaving(true);
    let done: boolean;
    if (heldGrant) done = await onChangeLevel(heldGrant, chosen, level);
    else done = await onGrant(chosen, level, target ? { id: target.id } : undefined);
    setSaving(false);
    if (done) close();
  };

  // The field editor's button is the Grant of a proposal or work agreement grant.
  const saveFields = async (fields: FieldAccessValues) => {
    if (!chosen || !target) return false;
    let done: boolean;
    if (target.held) done = await onChangeFields(target.held, chosen, fields);
    else done = await onGrant(chosen, undefined, { id: target.id, fields });
    if (done) close();
    return done;
  };

  const kindName = chosen ? kindLabel(chosen) : "";
  let submitLabel = "Grant";
  if (byField) submitLabel = target?.held ? "Change fields" : "Choose fields";
  else if (chosen) submitLabel = heldGrant ? "Change level" : `Grant ${chosen.name}`;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby={titleId}
      // Focus goes to the first field to fill, the search, once the dialog has opened — unless a kind
      // was chosen while it opened, whose target step has taken focus already.
      slotProps={{
        transition: {
          onEntered: () => {
            if (!chosen) searchRef.current?.focus();
          },
        },
      }}
    >
      <DialogTitle id={titleId}>Add access for {personName}</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Stepper activeStep={activeStep} sx={{ "& .MuiStepLabel-label": { typography: "body2" } }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <TextField
          inputRef={searchRef}
          fullWidth
          label="Find a kind of permission"
          value={text}
          onChange={(event) => setText(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, maxHeight: { sm: 360 }, overflowY: { sm: "auto" } }}>
          <List dense disablePadding aria-label="Kinds of permission">
            {areas.map((area) => {
              const inArea = visible.filter((kind) => kind.area === area);
              if (inArea.length === 0) return null;
              const token = permissionAreaToken(area);
              return (
                <li key={area}>
                  <ul style={{ padding: 0 }}>
                    <ListSubheader
                      sx={{
                        lineHeight: 2.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderLeft: 4,
                        borderColor: token.color,
                        backgroundColor: "grey.50",
                      }}
                    >
                      {area}
                    </ListSubheader>
                    {inArea.map((kind) => (
                      <ListItemButton
                        key={kind.slug}
                        selected={chosen?.slug === kind.slug}
                        onClick={() => choose(kind)}
                        sx={{ minHeight: 44, gap: 1, alignItems: "center" }}
                      >
                        <ListItemText
                          primary={kindLabel(kind)}
                          secondary={kindAllows(kind)}
                          slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                        />
                        {kind.perRecord && <Chip size="small" variant="outlined" label={NEEDS_A_RECORD} />}
                        {!kind.perRecord && held.has(kind.slug) && <Chip size="small" variant="outlined" label={ALREADY_HELD} />}
                      </ListItemButton>
                    ))}
                  </ul>
                </li>
              );
            })}
          </List>
          {visible.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {`No kind of permission matches "${text}".`}
            </Typography>
          )}
        </Box>
        {chosen?.perRecord && (
          // Keyed by kind, so another kind's step starts empty and takes focus again.
          <AddAccessTargetStep
            key={chosen.slug}
            personName={personName}
            kind={chosen}
            text={targetText}
            search={targetSearch}
            onText={setTargetText}
            chosen={target}
            onChoose={chooseTarget}
          />
        )}
        {chosen && showLevel && (
          <Box component="fieldset" ref={levelRef} sx={{ border: 0, p: 0, m: 0 }}>
            <Typography
              id={levelHeadingId}
              component="legend"
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {LEVEL_CONTROL_TITLE} · {target ? `${kindName}: ${target.name}` : kindName}
            </Typography>
            {options.length > 0 ? (
              <RadioGroup
                aria-labelledby={levelHeadingId}
                value={options.findIndex((option) => sameLevel(option.level, level))}
                onChange={(event) => setLevel(options[Number(event.target.value)].level)}
              >
                {options.map((option, index) => (
                  <FormControlLabel
                    key={option.label}
                    value={index}
                    control={<Radio sx={touchTargetSx()} />}
                    sx={{ alignItems: "flex-start", mr: 0, "& .MuiRadio-root": { mt: -0.5 } }}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.label}
                        </Typography>
                        {option.help && (
                          <Typography variant="caption" color="text.secondary">
                            {option.help}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            ) : (
              <Typography variant="body2">
                {HELD}: {personName} {kindAllows(chosen)}. This is the only level for this kind.
              </Typography>
            )}
            {heldGrant && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {personName} already holds {target ? `this ${noun}` : chosen.name}. Choosing a level here changes that grant.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={touchTargetSx("& .MuiButton-root")}>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!chosen || waitingForTarget || saving || unchanged || (!byField && heldGrant !== undefined && options.length === 0)}
          onClick={() => (byField ? setEditingFields(true) : void submit())}
        >
          {submitLabel}
        </Button>
      </DialogActions>
      {chosen && target && byField && (
        <FieldAccessDialog
          open={editingFields}
          kindName={kindName}
          personName={personName}
          targetName={target.name}
          fields={heldFields(target.held)}
          grantLabel={target.held ? undefined : `Grant ${chosen.name}`}
          onClose={() => setEditingFields(false)}
          onSave={saveFields}
        />
      )}
    </Dialog>
  );
}
